import { useI18n } from '../modules/i18n/i18nModule'

/**
 * The ApiErrorHandler class as plain functions.
 *
 * It was the root of the service inheritance chain purely so that AxiosService and
 * BaseApolloClientService could reach an injected i18n instance. Nothing about it needs
 * instance state, so it becomes free functions that read the i18n singleton directly.
 *
 * `src/modules/_base/apiErrorHandler.ts` still exports the class, unchanged, for the services
 * that extend it. The two are deliberately duplicated for now rather than having the class
 * delegate here: the class translates through its injected `TranslationService`, while these
 * functions read the module's own instance, which is not populated until each app's bootstrap
 * runs. Making the class delegate before then would silently break error translation. The
 * class goes away with those services in stage 3.
 *
 * One deliberate difference: the class logged the error code and its translation to the console
 * on every call. That debug noise is not carried over.
 */

export interface ErrorResponse {
  message: string
}

/**
 * Wrap a message as the error-shaped object the promise rejections carry.
 */
export const getErrorResponse = (errorMessage: string): ErrorResponse => {
  return {
    message: errorMessage
  }
}

/**
 * The API can answer with a bare error code; translate it.
 */
export const getErrorResponseFromCode = (code: string): ErrorResponse => {
  return getErrorResponse(useI18n().tc('system.errors.' + code))
}

/**
 * Is the API response a Bad Request (400)?
 */
export const isBadRequest = (r: any): boolean => {
  return !!r && r.response !== void 0 && r.response.status === 400
}

/**
 * Is the API response an auth failure (401/403)?
 */
export const isAuthError = (r: any): boolean => {
  return !!r && r.response !== void 0 && (r.response.status === 403 || r.response.status === 401)
}

/**
 * The API validates with class-validator. A constraint value is either a real message or a
 * translation key — a single word means it is a key (e.g. `invalidEmail`).
 */
export const getConstraintErrorResponse = (constraints: any): ErrorResponse => {
  const firstKey = Object.keys(constraints)[0]
  const message = constraints[firstKey]

  return firstKey.indexOf(' ') > -1
    ? getErrorResponse(message)
    : getErrorResponseFromCode(message)
}
